import { Wrench, Sparkles } from "lucide-react";
import logoFull from "@/assets/logo-full.webp";
import { BUSINESS_INFO } from "@/lib/business-info";

const EMBERS = Array.from({ length: 18 });
const PARTICLES = Array.from({ length: 24 });

/**
 * Màn bảo trì. `message` / `eta` do admin soạn ở `/admin/maintenance` (migration
 * v118) và tới đây qua `GET /v1/maintenance`.
 *
 * ⚠️ Cả hai đều TUỲ CHỌN, và câu mặc định phải luôn còn đó: trang này cũng hiện
 * trong ca đè bằng biến môi trường (`VITE_MAINTENANCE_MODE`), khi backend đã chết
 * hẳn và không ai lấy được lời nhắn từ đâu cả.
 */
export function MaintenancePage({ message, eta }: { message?: string; eta?: string } = {}) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-cosmic px-4">
      {/* Ambient breathing glow */}
      <div className="pointer-events-none absolute top-1/2 left-1/2 h-[700px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.08] blur-[120px] animate-mp-breathe" />
      <div className="pointer-events-none absolute top-1/2 left-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-purple-500/10 blur-[100px] animate-mp-breathe-slow" />

      {/* Drifting star particles */}
      <div className="pointer-events-none absolute inset-0">
        {PARTICLES.map((_, i) => {
          const left = (i * 37) % 100;
          const delay = (i * 0.7) % 8;
          const dur = 6 + ((i * 1.3) % 6);
          const size = 1 + (i % 3) * 0.5;
          return (
            <span
              key={`p-${i}`}
              className="absolute rounded-full bg-gold-soft/70 animate-mp-twinkle"
              style={{
                left: `${left}%`,
                top: `${(i * 53) % 100}%`,
                width: `${size}px`,
                height: `${size}px`,
                animationDelay: `${delay}s`,
                animationDuration: `${dur}s`,
                boxShadow: "0 0 6px oklch(0.88 0.09 88 / 0.8)",
              }}
            />
          );
        })}
      </div>

      {/* Rising magic embers */}
      <div className="pointer-events-none absolute inset-0">
        {EMBERS.map((_, i) => {
          const left = (i * 53 + 7) % 100;
          const delay = (i * 0.9) % 10;
          const dur = 8 + ((i * 1.7) % 8);
          const size = 3 + (i % 4);
          return (
            <span
              key={`e-${i}`}
              className="absolute bottom-0 rounded-full animate-mp-ember"
              style={{
                left: `${left}%`,
                width: `${size}px`,
                height: `${size}px`,
                background:
                  "radial-gradient(circle, oklch(0.88 0.18 75 / 0.9), oklch(0.78 0.18 25 / 0.4) 60%, transparent 75%)",
                filter: "blur(1px)",
                animationDelay: `${delay}s`,
                animationDuration: `${dur}s`,
              }}
            />
          );
        })}
      </div>

      {/* Card */}
      <div className="relative z-10 w-full max-w-md animate-mp-float">
        {/* Glow behind card */}
        <div className="pointer-events-none absolute -inset-6 rounded-3xl bg-gradient-to-t from-amber-500/10 via-primary/5 to-purple-500/10 blur-2xl animate-mp-breathe" />

        <div className="talisman relative p-8 text-center md:p-12">
          <div className="talisman-corner tl" />
          <div className="talisman-corner tr" />
          <div className="talisman-corner bl" />
          <div className="talisman-corner br" />

          {/* Rotating aura behind logo */}
          <div className="pointer-events-none absolute top-1/2 left-1/2 -z-10 h-64 w-64 -translate-x-1/2 -translate-y-[70%]">
            <div className="absolute inset-0 rounded-full bg-primary/10 blur-3xl animate-mp-breathe" />
            <div
              className="absolute inset-0 rounded-full opacity-40 animate-mp-rotate"
              style={{
                background:
                  "conic-gradient(from 0deg, transparent, oklch(0.82 0.14 85 / 0.25), transparent 40%, oklch(0.55 0.25 305 / 0.2), transparent 70%)",
                filter: "blur(20px)",
              }}
            />
          </div>

          <div className="relative z-10">
            {/* Logo with glow */}
            <div className="mx-auto mb-6 flex justify-center animate-mp-float-slow">
              <div className="relative">
                <div className="pointer-events-none absolute inset-0 -m-4 rounded-full bg-primary/20 blur-2xl animate-mp-glow" />
                <img
                  src={logoFull}
                  alt="Bói Đê"
                  className="relative h-14 w-auto object-contain drop-shadow-[0_0_18px_oklch(0.82_0.14_85/0.6)]"
                />
              </div>
            </div>

            {/* Icon */}
            <div className="relative mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-gold-soft/40 bg-primary/10 shadow-[0_0_20px_oklch(0.82_0.14_85/0.25)] animate-mp-glow">
              <span className="absolute inset-0 rounded-full border border-gold-soft/30 animate-mp-ring" />
              <Wrench className="h-6 w-6 text-primary animate-mp-wrench" />
            </div>

            {/* Heading */}
            <h1 className="font-serif-soft text-2xl text-gold-gradient md:text-3xl">
              Hệ thống đang bảo trì
            </h1>

            {/* Subtext */}
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              {message ? (
                message
              ) : (
                <>
                  Bói Đê đang được tu bổ để mang đến trải nghiệm tốt hơn.
                  <br />
                  Xin quý khách quay lại sau giây lát.
                </>
              )}
            </p>
            {eta ? <p className="mt-3 text-sm text-gold-soft/90">Dự kiến mở lại: {eta}</p> : null}

            {/* Decorative divider */}
            <div className="mx-auto mt-6 flex items-center justify-center gap-3">
              <span className="inline-block h-px w-12 bg-gradient-to-r from-transparent to-gold-soft/40" />
              <Sparkles className="h-4 w-4 text-gold-soft/80 animate-mp-spin-slow" />
              <span className="inline-block h-px w-12 bg-gradient-to-l from-transparent to-gold-soft/40" />
            </div>

            {/* Đơn vị vận hành */}
            <div className="mt-5 text-xs tracking-widest uppercase text-muted-foreground/50">
              {BUSINESS_INFO.name}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes mp-ember {
          0% { transform: translateY(0) translateX(0) scale(0.6); opacity: 0; }
          15% { opacity: 0.9; }
          100% { transform: translateY(-110vh) translateX(var(--mp-drift, 30px)) scale(1.2); opacity: 0; }
        }
        @keyframes mp-twinkle {
          0%, 100% { opacity: 0.15; transform: scale(0.8); }
          50% { opacity: 0.9; transform: scale(1.2); }
        }
        @keyframes mp-breathe {
          0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
        }
        @keyframes mp-breathe-slow {
          0%, 100% { opacity: 0.4; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.15); }
        }
        @keyframes mp-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes mp-float-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @keyframes mp-glow {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes mp-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes mp-ring {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes mp-wrench {
          0%, 100% { transform: rotate(-8deg); }
          50% { transform: rotate(8deg); }
        }
        @keyframes mp-spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-mp-ember { animation: mp-ember linear infinite; }
        .animate-mp-twinkle { animation: mp-twinkle ease-in-out infinite; }
        .animate-mp-breathe { animation: mp-breathe 5s ease-in-out infinite; }
        .animate-mp-breathe-slow { animation: mp-breathe-slow 8s ease-in-out infinite; }
        .animate-mp-float { animation: mp-float 6s ease-in-out infinite; }
        .animate-mp-float-slow { animation: mp-float-slow 4s ease-in-out infinite; }
        .animate-mp-glow { animation: mp-glow 3s ease-in-out infinite; }
        .animate-mp-rotate { animation: mp-rotate 18s linear infinite; }
        .animate-mp-ring { animation: mp-ring 2.4s ease-out infinite; }
        .animate-mp-wrench { animation: mp-wrench 3s ease-in-out infinite; transform-origin: center; }
        .animate-mp-spin-slow { animation: mp-spin-slow 8s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .animate-mp-ember, .animate-mp-twinkle, .animate-mp-breathe, .animate-mp-breathe-slow,
          .animate-mp-float, .animate-mp-float-slow, .animate-mp-glow, .animate-mp-rotate,
          .animate-mp-ring, .animate-mp-wrench, .animate-mp-spin-slow {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
